import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bell,
  BookOpen,
  Building2,
  ClipboardList,
  ExternalLink,
  FileText,
  Loader2,
  MapPin,
  Package,
  Pencil,
  Receipt,
  Save,
  Settings,
  ShoppingCart,
  Truck,
  Users,
  Warehouse,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { farmGet, farmPost, farmPatch, farmDelete, farmPut } from "./shared";
import ModelliContrattiPrivacy from "./ModelliContrattiPrivacy";

const NAV_SECTIONS = [
  { id: "profilo", label: "Profilo farmacia", icon: Building2 },
  { id: "sede", label: "Sede e geocoding", icon: MapPin },
  { id: "solleciti", label: "Ricette e solleciti", icon: Settings },
  { id: "utenti", label: "Utenti e ruoli", icon: Users },
  { id: "modelli", label: "Modelli contratto/privacy", icon: FileText },
  { id: "moduli", label: "Moduli collegati", icon: ClipboardList },
  { id: "guida", label: "Documentazione", icon: BookOpen },
];

function SettingCard({ icon: Icon, title, description, rows, to, onClick, tone }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    violet: "bg-violet-50 text-violet-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    slate: "bg-slate-100 text-slate-600",
  };
  const Comp = to ? Link : "button";
  const props = to
    ? { to }
    : { type: "button", onClick };
  return (
    <Comp
      {...props}
      className="flex h-full flex-col rounded-2xl border border-slate-200/80 bg-white p-4 text-left shadow-sm transition hover:border-emerald-200 hover:shadow"
    >
      <div className="flex items-start gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone] || tones.emerald}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          <p className="mt-0.5 text-[11px] leading-snug text-slate-500">{description}</p>
        </div>
      </div>
      {rows?.length ? (
        <ul className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-600">
          {rows.map((r) => (
            <li key={r.label} className="flex justify-between gap-2">
              <span>{r.label}</span>
              <span className={`font-medium ${r.alert ? "text-rose-600" : "text-slate-900"}`}>
                {r.value}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </Comp>
  );
}

export default function ImpostazioniFarmacia() {
  const { token, user, hasPermission } = useFarmaciaAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("profilo");
  const [editingSede, setEditingSede] = useState(false);
  const [config, setConfig] = useState(null);
  const [sede, setSede] = useState({
    via: "",
    civico: "",
    cap: "",
    comune: "",
    provincia: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSede, setSavingSede] = useState(false);
  const [stats, setStats] = useState({
    strutture: 0,
    clienti: 0,
    prodotti: 0,
    scorteBasse: 0,
    fattureAperte: 0,
    consegne: 0,
    notificheUnread: 0,
  });

  const canManageUsers = !!user?.utenti_ruoli_v1 && hasPermission("utenti:manage");
  const [utenti, setUtenti] = useState([]);
  const [ruoli, setRuoli] = useState([]);
  const [utentiLoading, setUtentiLoading] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    email: "",
    password: "",
    ruolo: "farmacista",
  });
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [cfgRes, sedeRes, kpiRes, cliRes, catRes, notRes, consRes] = await Promise.all([
        farmGet("/farmacia/config", token),
        farmGet("/farmacia/sede", token),
        farmGet("/farmacia/ricette/kpi", token).catch(() => ({})),
        farmGet("/farmacia/clienti", token).catch(() => ({ items: [] })),
        farmGet("/farmacia/prodotti", token, { page: 1, limit: 1 }).catch(() => ({ total: 0 })),
        farmGet("/farmacia/notifiche", token, { limit: 1 }).catch(() => ({ unread: 0 })),
        farmGet("/farmacia/consegne", token).catch(() => ({ items: [] })),
      ]);
      setConfig(cfgRes.config || {});
      setSede({
        via: sedeRes.via || "",
        civico: sedeRes.civico || "",
        cap: sedeRes.cap || "",
        comune: sedeRes.comune || "",
        provincia: sedeRes.provincia || "",
        sede_lat: sedeRes.sede_lat,
        sede_lng: sedeRes.sede_lng,
        indirizzo: sedeRes.indirizzo || "",
        ok: sedeRes.ok,
      });
      setStats({
        strutture: kpiRes.strutture ?? user?.strutture_count ?? 0,
        clienti: typeof kpiRes.clienti_diretti === "number" ? kpiRes.clienti_diretti : (cliRes.items || []).length,
        prodotti: catRes.total ?? (catRes.items || []).length ?? 0,
        scorteBasse: kpiRes.scorte_basse ?? 0,
        fattureAperte: kpiRes.fatture_aperte ?? 0,
        consegne: (consRes.items || []).length,
        notificheUnread: notRes.unread ?? 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [token, user?.strutture_count]);

  const loadUtenti = useCallback(async () => {
    if (!token || !canManageUsers) return;
    setUtentiLoading(true);
    try {
      const res = await farmGet("/farmacia/utenti", token);
      setUtenti(res.items || []);
      setRuoli(res.ruoli || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento utenti");
    } finally {
      setUtentiLoading(false);
    }
  }, [token, canManageUsers]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadUtenti();
  }, [loadUtenti]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await farmPut("/farmacia/config", token, config);
      setConfig(res.config);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio fallito");
    } finally {
      setSaving(false);
    }
  };

  const saveSede = async () => {
    setSavingSede(true);
    try {
      const res = await farmPut("/farmacia/sede", token, {
        via: sede.via,
        civico: sede.civico,
        cap: sede.cap,
        comune: sede.comune,
        provincia: sede.provincia,
      });
      setSede({
        via: res.via || "",
        civico: res.civico || "",
        cap: res.cap || "",
        comune: res.comune || "",
        provincia: res.provincia || "",
        sede_lat: res.sede_lat,
        sede_lng: res.sede_lng,
        indirizzo: res.indirizzo || "",
        ok: res.geocoded,
      });
      setEditingSede(false);
      toast.success(res.geocoded ? "Sede salvata e geocodificata" : "Sede salvata (geocoding non riuscito)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio sede fallito");
    } finally {
      setSavingSede(false);
    }
  };

  const createUtente = async () => {
    if (!form.nome.trim() || !form.email.trim() || !form.password) {
      toast.error("Compila nome, email e password");
      return;
    }
    setCreating(true);
    try {
      await farmPost("/farmacia/utenti", token, form);
      toast.success("Utente creato");
      setForm({ nome: "", email: "", password: "", ruolo: "farmacista" });
      await loadUtenti();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Creazione fallita");
    } finally {
      setCreating(false);
    }
  };

  const changeRuolo = async (u, ruolo) => {
    setBusyId(u.id);
    try {
      await farmPatch(`/farmacia/utenti/${encodeURIComponent(u.id)}`, token, { ruolo });
      toast.success("Ruolo aggiornato");
      await loadUtenti();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento fallito");
    } finally {
      setBusyId(null);
    }
  };

  const toggleAttivo = async (u) => {
    setBusyId(u.id);
    try {
      if (u.attivo) {
        await farmDelete(`/farmacia/utenti/${encodeURIComponent(u.id)}`, token);
        toast.success("Utente disattivato");
      } else {
        await farmPatch(`/farmacia/utenti/${encodeURIComponent(u.id)}`, token, { attivo: true });
        toast.success("Utente riattivato");
      }
      await loadUtenti();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione fallita");
    } finally {
      setBusyId(null);
    }
  };

  const goSection = (id) => {
    setSection(id);
    const el = document.getElementById(`imp-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const update = (k, v) => setConfig((c) => ({ ...c, [k]: v }));
  const updateSede = (k, v) => setSede((s) => ({ ...s, [k]: v }));

  const navItems = useMemo(
    () => NAV_SECTIONS.filter((n) => n.id !== "utenti" || canManageUsers),
    [canManageUsers]
  );

  const utentiAttivi = utenti.filter((u) => u.attivo !== false).length;
  const ruoliCount = ruoli.length;
  const features = config?.features || user?.config?.features || {};
  const farmaciaNome = user?.nome || "Farmacia";
  const farmaciaEmail = user?.email || "—";
  const initials = (farmaciaNome || "F")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  const indirizzoLabel =
    sede.indirizzo ||
    [sede.via, sede.civico, sede.cap, sede.comune, sede.provincia].filter(Boolean).join(", ") ||
    "Indirizzo non configurato";

  if (loading || !config) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
        <aside className="w-full shrink-0 lg:w-52">
          <nav className="sticky top-0 space-y-0.5 rounded-2xl border border-slate-200/80 bg-white p-2 shadow-sm">
            {navItems.map((n) => {
              const Icon = n.icon;
              const active = section === n.id;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => goSection(n.id)}
                  className={`flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-xs font-medium transition ${
                    active
                      ? "bg-[#D4EFDC] text-[#0F5C4C]"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  {n.label}
                </button>
              );
            })}
          </nav>
        </aside>

        <div className="min-w-0 flex-1 space-y-5">
          <section
            id="imp-profilo"
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#0F5C4C] text-lg font-semibold text-white">
                  {initials}
                </span>
                <div>
                  <h2 className="text-base font-semibold text-slate-900">{farmaciaNome}</h2>
                  <p className="mt-0.5 text-xs text-slate-500">Profilo farmacia</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {sede.ok ? (
                      <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700">
                        Sede geocodificata
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-amber-200 bg-amber-50 text-[10px] text-amber-800">
                        Sede da completare
                      </Badge>
                    )}
                    {user?.utenti_ruoli_v1 ? (
                      <Badge variant="outline" className="text-[10px]">
                        Ruolo: {user.ruolo_label || user.ruolo || "amministratore"}
                      </Badge>
                    ) : null}
                  </div>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-xl"
                onClick={() => {
                  setEditingSede(true);
                  goSection("sede");
                }}
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                Modifica sede
              </Button>
            </div>

            <dl className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-xl bg-slate-50 px-3 py-2.5">
                <dt className="text-[10px] uppercase tracking-wide text-slate-400">Nome farmacia</dt>
                <dd className="mt-0.5 text-sm font-medium text-slate-800">{farmaciaNome}</dd>
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-2.5">
                <dt className="text-[10px] uppercase tracking-wide text-slate-400">Email account</dt>
                <dd className="mt-0.5 truncate text-sm font-medium text-slate-800">{farmaciaEmail}</dd>
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-2.5 sm:col-span-2 lg:col-span-1">
                <dt className="text-[10px] uppercase tracking-wide text-slate-400">Indirizzo sede</dt>
                <dd className="mt-0.5 text-sm font-medium text-slate-800">{indirizzoLabel}</dd>
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-2.5">
                <dt className="text-[10px] uppercase tracking-wide text-slate-400">Strutture collegate</dt>
                <dd className="mt-0.5 text-sm font-medium text-slate-800">{stats.strutture}</dd>
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-2.5">
                <dt className="text-[10px] uppercase tracking-wide text-slate-400">Email mittente</dt>
                <dd className="mt-0.5 truncate text-sm font-medium text-slate-800">
                  {config.email_mittente || "Non impostata"}
                </dd>
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-2.5">
                <dt className="text-[10px] uppercase tracking-wide text-slate-400">Solleciti automatici</dt>
                <dd className="mt-0.5 text-sm font-medium text-slate-800">
                  {config.solleciti_automatici ? "Attivi" : "Disattivi"}
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-[11px] text-slate-400">
              Nome, P.IVA, logo, orari e codice SDI non sono gestiti da questa schermata (account farmacia).
            </p>
          </section>

          <section
            id="imp-sede"
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm"
          >
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <MapPin className="h-4 w-4 text-emerald-600" />
                  Sede e geocoding
                </h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  Indirizzo usato come partenza/arrivo dei percorsi in Consegne.
                </p>
              </div>
              {!editingSede ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                  onClick={() => setEditingSede(true)}
                >
                  Modifica
                </Button>
              ) : null}
            </div>

            {editingSede ? (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="sm:col-span-2">
                    <label className="text-xs font-medium text-slate-600">Via</label>
                    <Input className="mt-1" value={sede.via} onChange={(e) => updateSede("via", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Civico</label>
                    <Input className="mt-1" value={sede.civico} onChange={(e) => updateSede("civico", e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">CAP</label>
                    <Input className="mt-1" value={sede.cap} onChange={(e) => updateSede("cap", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Comune</label>
                    <Input className="mt-1" value={sede.comune} onChange={(e) => updateSede("comune", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Provincia</label>
                    <Input
                      className="mt-1"
                      maxLength={2}
                      placeholder="RM"
                      value={sede.provincia}
                      onChange={(e) => updateSede("provincia", e.target.value.toUpperCase())}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={saveSede}
                    disabled={savingSede}
                    className="rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]"
                  >
                    {savingSede ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Salva sede
                  </Button>
                  <Button type="button" variant="ghost" className="rounded-xl" onClick={() => setEditingSede(false)}>
                    Annulla
                  </Button>
                </div>
              </div>
            ) : (
              <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <p>{indirizzoLabel}</p>
                {sede.ok ? (
                  <p className="mt-1 text-xs text-emerald-700">
                    Geocodificata ({Number(sede.sede_lat).toFixed(5)}, {Number(sede.sede_lng).toFixed(5)})
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-amber-700">
                    Geocoding assente: salva un indirizzo completo per abilitare i percorsi.
                  </p>
                )}
              </div>
            )}
          </section>

          <section
            id="imp-solleciti"
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm"
          >
            <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Settings className="h-4 w-4 text-emerald-600" />
              Ricette e solleciti
            </h2>
            <p className="mb-4 text-xs text-slate-500">
              Scadenze, solleciti automatici e mittente email per clienti diretti / fatture.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs font-medium text-slate-600">Giorni scadenza ricette</label>
                <Input
                  type="number"
                  min={1}
                  className="mt-1"
                  value={config.giorni_scadenza_ricette ?? 30}
                  onChange={(e) => update("giorni_scadenza_ricette", parseInt(e.target.value, 10) || 30)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Giorni attesa prossimo Carepy</label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  className="mt-1"
                  value={config.giorni_attesa_carepy ?? 30}
                  onChange={(e) => update("giorni_attesa_carepy", parseInt(e.target.value, 10) || 30)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Giorni minimi tra solleciti</label>
                <Input
                  type="number"
                  min={0}
                  className="mt-1"
                  value={config.giorni_sollecito ?? 3}
                  onChange={(e) => update("giorni_sollecito", parseInt(e.target.value, 10) || 0)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Max solleciti per richiesta</label>
                <Input
                  type="number"
                  min={1}
                  className="mt-1"
                  value={config.max_solleciti ?? 3}
                  onChange={(e) => update("max_solleciti", parseInt(e.target.value, 10) || 3)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Email mittente</label>
                <Input
                  type="email"
                  className="mt-1"
                  placeholder="es. ricette@tuafarmacia.it"
                  value={config.email_mittente || ""}
                  onChange={(e) => update("email_mittente", e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Casella ricezione ricette e documenti</label>
                <Input
                  type="email"
                  className="mt-1"
                  placeholder="es. ricette@email.affidya.it"
                  value={config.email_inbound_ricette || ""}
                  onChange={(e) => update("email_inbound_ricette", e.target.value)}
                />
                <p className="mt-1 text-[11px] text-slate-400">Le ricette da strutture esterne vengono abbinate per codice fiscale e messe in revisione.</p>
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!config.solleciti_automatici}
                    onChange={(e) => update("solleciti_automatici", e.target.checked)}
                  />
                  <span>Solleciti automatici attivi</span>
                </label>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">Firma email</label>
                <textarea
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  rows={2}
                  value={config.firma || ""}
                  onChange={(e) => update("firma", e.target.value)}
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Per gli ospiti di struttura l&apos;email parte a nome della struttura (risposte nel webhook Affidya).
                </p>
              </div>
            </div>
            <Button
              onClick={save}
              disabled={saving}
              className="mt-4 rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Salva configurazione
            </Button>
          </section>

          {canManageUsers ? (
            <section
              id="imp-utenti"
              className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm"
            >
              <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Users className="h-4 w-4 text-emerald-600" />
                Utenti e ruoli
              </h2>
              <p className="mb-4 text-xs text-slate-500">
                L&apos;account farmacia resta amministratore. Qui crei operatori con permessi distinti.
              </p>
              <div className="mb-3 grid gap-2 sm:grid-cols-2">
                <Input
                  placeholder="Nome"
                  value={form.nome}
                  onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                />
                <select
                  className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm"
                  value={form.ruolo}
                  onChange={(e) => setForm((f) => ({ ...f, ruolo: e.target.value }))}
                >
                  {ruoli.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                onClick={createUtente}
                disabled={creating}
                className="mb-4 rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea utente"}
              </Button>

              {utentiLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
                </div>
              ) : utenti.length === 0 ? (
                <p className="py-4 text-center text-xs text-slate-400">Nessun operatore creato</p>
              ) : (
                <ul className="divide-y divide-slate-100 rounded-xl border border-slate-100">
                  {utenti.map((u) => (
                    <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm font-medium text-slate-800">{u.nome}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {u.ruolo_label}
                          </Badge>
                          {!u.attivo ? (
                            <Badge variant="outline" className="text-[10px] text-rose-600">
                              Disattivo
                            </Badge>
                          ) : null}
                        </div>
                        <p className="truncate text-xs text-slate-500">{u.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs"
                          value={u.ruolo}
                          disabled={busyId === u.id}
                          onChange={(e) => changeRuolo(u, e.target.value)}
                        >
                          {ruoli.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === u.id}
                          onClick={() => toggleAttivo(u)}
                        >
                          {u.attivo ? "Disattiva" : "Riattiva"}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}

          <section id="imp-modelli" className="space-y-4">
            <ModelliContrattiPrivacy />
            <p className="text-xs text-slate-500">
              I documenti firmati (contratto e privacy) si gestiscono anche da{" "}
              <Link className="text-emerald-700 underline" to="/farmacia/amministrazione?tab=documenti">
                Amministrazione → Documenti
              </Link>{" "}
              e dalla panoramica di ogni struttura. Le strutture li caricano da Deblistering → Impostazioni.
            </p>
          </section>

          <section id="imp-moduli">
            <h2 className="mb-3 text-sm font-semibold text-slate-800">Moduli collegati</h2>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {canManageUsers ? (
                <SettingCard
                  icon={Users}
                  title="Utenti e ruoli"
                  description="Operatori e permessi del portale"
                  tone="emerald"
                  onClick={() => goSection("utenti")}
                  rows={[
                    { label: "Utenti attivi", value: utentiAttivi },
                    { label: "Ruoli disponibili", value: ruoliCount || "—" },
                  ]}
                />
              ) : null}
              <SettingCard
                icon={Building2}
                title="Strutture collegate"
                description="RSA e convenzioni gestite dal collegamento Affidya"
                tone="sky"
                to="/farmacia/strutture"
                rows={[
                  { label: "Strutture collegate", value: stats.strutture },
                  { label: "Gestione collegamento", value: "Admin Affidya" },
                ]}
              />
              <SettingCard
                icon={Users}
                title="Clienti diretti"
                description="Pazienti privati della farmacia"
                tone="violet"
                to="/farmacia/clienti"
                rows={[
                  { label: "Clienti attivi", value: stats.clienti },
                  { label: "Ambito", value: "Domicilio / privato" },
                ]}
              />
              <SettingCard
                icon={ShoppingCart}
                title="Ordini"
                description="Coda ordini extra blister"
                tone="amber"
                to="/farmacia/ordini"
                rows={[
                  { label: "Modulo", value: features.ordini_extra === false ? "Off" : "On" },
                  { label: "Apri coda", value: "Vai →" },
                ]}
              />
              <SettingCard
                icon={Warehouse}
                title="Magazzino"
                description="Catalogo prodotti e scorte"
                tone="emerald"
                to="/farmacia/magazzino"
                rows={[
                  { label: "Prodotti (catalogo)", value: stats.prodotti },
                  { label: "Scorte basse", value: stats.scorteBasse, alert: stats.scorteBasse > 0 },
                ]}
              />
              <SettingCard
                icon={Truck}
                title="Consegne"
                description="Pianificazione e vista autista"
                tone="sky"
                to="/farmacia/consegne"
                rows={[
                  { label: "Consegne recenti", value: stats.consegne },
                  { label: "Sede geocodificata", value: sede.ok ? "Sì" : "No", alert: !sede.ok },
                ]}
              />
              <SettingCard
                icon={Receipt}
                title="Fatturazione"
                description="Fatture PDF e riepiloghi extra"
                tone="violet"
                to="/farmacia/amministrazione"
                rows={[
                  { label: "Fatture aperte", value: stats.fattureAperte },
                  { label: "Modulo", value: features.fatture_v1 === false ? "Off" : "On" },
                ]}
              />
              <SettingCard
                icon={Bell}
                title="Notifiche"
                description="Campanella in-app e solleciti email"
                tone="amber"
                onClick={() => goSection("solleciti")}
                rows={[
                  {
                    label: "Notifiche in-app",
                    value: features.notifiche_v1 === false ? "Off" : "On",
                  },
                  { label: "Non lette", value: stats.notificheUnread },
                  {
                    label: "Solleciti auto",
                    value: config.solleciti_automatici ? "Attivi" : "Off",
                  },
                ]}
              />
              <SettingCard
                icon={FileText}
                title="Documenti essenziali"
                description="Contratti e privacy firmati sulle strutture"
                tone="slate"
                to="/farmacia/amministrazione?tab=documenti"
                rows={[
                  {
                    label: "Modulo",
                    value: features.documenti_essenziali === false ? "Off" : "On",
                  },
                  { label: "Gestione", value: "Amministrazione" },
                ]}
              />
              <SettingCard
                icon={FileText}
                title="Modelli contratto/privacy"
                description="Blank scaricabili dalle strutture per firma"
                tone="sky"
                onClick={() => goSection("modelli")}
                rows={[
                  { label: "Tipi", value: "Contratto + Privacy" },
                  { label: "Condivisione", value: "Bidirezionale" },
                ]}
              />
              <SettingCard
                icon={Package}
                title="Report"
                description="Andamento operativo aggregato"
                tone="emerald"
                to="/farmacia/report"
                rows={[
                  { label: "Periodi", value: "7 / 30 / 90 gg" },
                  { label: "Export", value: "CSV" },
                ]}
              />
              <SettingCard
                icon={BookOpen}
                title="Documentazione"
                description="Guida operativa del Portale Farmacia"
                tone="sky"
                onClick={() => goSection("guida")}
                rows={[
                  { label: "Formato", value: "HTML" },
                  { label: "Apri", value: "Sotto →" },
                ]}
              />
              <SettingCard
                icon={ClipboardList}
                title="Pratiche / Carepy"
                description="Flusso pratiche e richieste Carepy"
                tone="violet"
                to="/farmacia/pratiche"
                rows={[
                  { label: "Carepy", value: features.carepy_import === false ? "Off" : "On" },
                  { label: "Controllo ricette", value: features.ricette_controllo === false ? "Off" : "On" },
                ]}
              />
            </div>
            <p className="mt-3 text-[11px] text-slate-400">
              Non presenti in Affidya: logo/orari, fornitori/listini, integrazioni esterne, 2FA, backup automatico,
              campi personalizzati. Le card sopra aprono solo funzioni reali.
            </p>
          </section>

          <section
            id="imp-guida"
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm"
          >
            <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800">
              <BookOpen className="h-4 w-4 text-emerald-600" />
              Documentazione
            </h2>
            <p className="mb-3 text-xs text-slate-500">Guida operativa del Portale Farmacia.</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="rounded-xl" asChild>
                <a href="/documents/GUIDA_PORTALE_FARMACIA.html" target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Apri guida HTML
                </a>
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="rounded-xl"
                onClick={() => navigate("/farmacia")}
              >
                Torna alla dashboard
              </Button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
